import React from 'react';
import styled from 'styled-components';
import { COLOR } from '../constant-styles';

// Styled component para cada línea de texto
const Line = styled.div<{
	fontSize: number;
	lineHeight: number;
	isFirst: boolean;
	hasSpace: boolean;
	hasElement: boolean;
	numberOfWords: number;
	numberOfLetters: number;
	hasOneCarline: boolean;
}>`
	font-size: ${({ fontSize }) => fontSize}px;
	font-size: ${(props) =>
		(props.hasSpace || props.numberOfWords > 1) && props.fontSize - 10}px;
	font-size: ${(props) => props.hasElement && props.fontSize - 40}px;

	${({ lineHeight, isFirst }) => `
		line-height: ${lineHeight}px;
		${isFirst ? 'margin-top: 0;' : ''};
	`};

	@media screen and (max-width: 560px) {
		font-size: ${(props) =>
			props.hasElement && props.numberOfLetters < 6 && props.fontSize + 50}px;
		font-size: ${(props) =>
			props.hasElement && props.numberOfLetters > 4 && props.fontSize + 10}px;
	}

	// Tablet size
	@media screen and (min-width: 960px) and (max-width: 1199px) {
		width: 100%;
		text-align: start;
		font-size: ${(props) =>
			props.isFirst && props.numberOfLetters === 5 && props.fontSize + 30}px;
		font-size: ${(props) =>
			props.isFirst && props.numberOfLetters > 5 && props.fontSize + 20}px;
		font-size: ${(props) =>
			!props.isFirst && props.numberOfLetters === 5 && props.fontSize + 60}px;
		font-size: ${(props) =>
			props.hasElement && props.numberOfLetters < 5 && props.fontSize + 80}px;
	}

	@media screen and (min-width: 1200px) {
		font-size: ${(props) =>
			props.hasElement && props.numberOfLetters < 5 && props.fontSize + 120}px;
		font-size: ${(props) => props.numberOfLetters > 4 && props.fontSize + 10}px;
	}
`;

// Styled component para el contenedor del texto
const TextContainer = styled.div<{ hasOneCarline: boolean }>`
	font-family: var(--font-family-head);
	font-weight: bold;
	color: transparent;
	-webkit-text-stroke: 3px ${COLOR.STROKE};
	z-index: -2;

	height: 100%;
	width: 100%;
	align-content: center;
	//overflow: hidden;

	display: flex;
	flex-direction: column;
	justify-content: center;
	align-content: flex-start;
	align-items: flex-start;

	@media screen and (max-width: 559px) {
		position: absolute;
		top: 0;
		height: 27%;
	}

	@media screen and (min-width: 560px) and (max-width: 959px) {
		position: absolute;
		height: 22%;
	}

	// Tablet size
	@media screen and (min-width: 960px) and (max-width: 1199px) {
		position: absolute;
		top: ${(props) => props.theme.size.dynamic0270};
		left: 0;
		width: 100%;
		height: 35%;
		align-content: flex-start;
		align-items: flex-start;
		gap: 5%;
		padding-right: var(--size-grid001);
		padding-left: var(--size-grid004);

		height: ${(props) => props.hasOneCarline && '25vh'};
		overflow: ${(props) => props.hasOneCarline && 'visible'};
		padding: ${(props) => props.hasOneCarline && '0'};
		top: ${(props) => props.hasOneCarline && 'var(--size-dynamic0120)'};
		position: ${(props) => props.hasOneCarline && 'unset'};
	}

	@media screen and (min-width: 1200px) {
		gap: 3%;
		overflow: visible;
		padding-left: 10%;
	}
`;

const ModelTitleBackground = ({
	text,
	hasOneCarline,
}: {
	text: string;
	hasOneCarline: boolean;
}): React.ReactElement => {
	const mergeLastTwoWords = (text: string): string[] => {
		if (!text) {
			return [];
		}

		const words = text.split(' ');

		if (words.length < 2) {
			return words;
		}

		// Si el texto completo tiene 8 caracteres o menos (sin contar espacios), no dividir
		const textWithoutSpaces = text.replace(/\s/g, '');
		if (textWithoutSpaces.length <= 8) {
			return [text];
		}

		// Si hay 3 o más palabras, dividir dejando la última palabra sola
		if (words.length >= 3) {
			const firstPart = words.slice(0, -1).join(' ');
			const lastWord = words[words.length - 1];
			return [firstPart, lastWord];
		}

		// Si hay 2 palabras, dividir normalmente
		return words;
	};

	const words = mergeLastTwoWords(text);

	const hasSpace = (text: string): boolean => {
		return text.includes(' ');
	};

	const hasElement = (text: string): boolean => {
		return text.includes('.') || text.includes('‑');
	};

	const [fontSizes, setFontSizes] = React.useState([]);
	const [lineHeight, setLineHeight] = React.useState(0);

	React.useEffect(() => {
		const sizes = words.map((word) => {
			const testDiv = document.createElement('div');

			testDiv.style.visibility = 'hidden';
			testDiv.style.position = 'absolute';
			testDiv.style.whiteSpace = 'nowrap';
			testDiv.style.fontSize = '100px'; // Empezamos con un tamaño grande
			testDiv.innerHTML = word;
			document.body.appendChild(testDiv);

			const width = testDiv.offsetWidth;

			let fontSize: number;

			if (window.innerWidth < 960) {
				fontSize = (window.innerWidth / width) * 65;
			}

			if (window.innerWidth >= 960) {
				fontSize = (window.innerWidth / width) * 47;
			}

			if (window.innerWidth >= 1200) {
				fontSize = (window.innerWidth / width) * 40;
			}

			document.body.removeChild(testDiv);
			return fontSize;
		});

		setFontSizes(sizes);

		// Calcular el interlineado en función del tamaño de la fuente más grande
		const maxFontSize = Math.max(...sizes);
		setLineHeight(maxFontSize * 0.75); // Ejemplo: 20% mayor que el tamaño de la fuente más grande
	}, [text]);

	return (
		<TextContainer hasOneCarline={hasOneCarline}>
			{words.map((word, index) => (
				<Line
					className="model-title-background__line"
					key={index}
					fontSize={fontSizes[index]}
					lineHeight={lineHeight}
					isFirst={index === 0}
					hasSpace={hasSpace(word)}
					hasElement={hasElement(word)}
					numberOfWords={words.length}
					numberOfLetters={word.length}
					hasOneCarline={hasOneCarline}
				>
					{word.toLocaleUpperCase()}
				</Line>
			))}
		</TextContainer>
	);
};

export default ModelTitleBackground;
